/**
 * Building blocks shared by the interactive report's sections (16 Oct: moved
 * out of `insights-view.tsx` so the new views in `insights-more.tsx` use the
 * same ones): the ⓘ definition popover, a section card, a sub-title and a
 * number that opens its tickets.
 */

import { Icon, Info } from "@norican/icons";
import { Popover, Typography } from "@norican/react";
import { type ReactNode, useRef } from "react";

import { useT } from "@/i18n";

import type { ExportSection } from "./export-helpers";
import { ExportMenu } from "./insights-export";

/** ⓘ: the exact definition of a number. */
export function Definition({ text, label }: { text: string; label: string }) {
  const { t } = useT("common");
  return (
    <Popover.Root>
      <Popover.Trigger
        render={
          <button
            type="button"
            className="text-fg-muted hover:text-fg focus-visible:ring-accent inline-flex rounded-full align-middle focus-visible:ring-2 focus-visible:outline-none"
            aria-label={t("leadershipReport.live.definitionOf", { name: label })}
            data-print-hide
          />
        }
      >
        <Icon icon={Info} size="xs" />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Positioner side="bottom" align="start">
          <Popover.Popup className="max-w-sm">
            <Popover.Body>
              <Popover.Title>{label}</Popover.Title>
              <Popover.Description>{text}</Popover.Description>
            </Popover.Body>
            <Popover.Arrow />
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}

/** A section card; `exportAs` (28 Oct) adds its "Export" menu (picture + its Excel sheets). */
export function Section({
  title,
  definition,
  exportAs,
  children,
}: {
  title: string;
  definition: string;
  exportAs?: ExportSection;
  children: ReactNode;
}) {
  const ref = useRef<HTMLElement>(null);
  return (
    <section
      ref={ref}
      className="border-border bg-bg break-inside-avoid rounded-xl border px-5 py-4"
      data-print-avoid-break
    >
      <div className="mb-2.5 flex items-center gap-1.5">
        <Typography as="h2" variant="heading-6" className="text-[#0b4f8a]">
          {title}
        </Typography>
        <Definition text={definition} label={title} />
        {exportAs ? (
          <span className="ms-auto">
            <ExportMenu title={title} target={ref} section={exportAs} />
          </span>
        ) : null}
      </div>
      {children}
    </section>
  );
}

export function SubTitle({ text, definition }: { text: string; definition: string }) {
  return (
    <div className="mb-1.5 flex items-center gap-1">
      <h3 className="text-[13.5px] font-semibold">{text}</h3>
      <Definition text={definition} label={text} />
    </div>
  );
}

export function LinkNumber({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="hover:text-fg focus-visible:ring-accent rounded underline-offset-2 hover:underline focus-visible:ring-2 focus-visible:outline-none"
    >
      {children}
    </button>
  );
}
